import type { ManageAPI } from "~/manage-api/types";
import { fisherYatesShuffle } from "./array";

export function getOneOfferPerStore<T extends ManageAPI.Offer>(offers: T[]): T[] {
  try {
    let showOffers: T[] = [];
    for (let i = 0; i < offers.length; i++) {
      let idExisted = showOffers.find((o) => o?.store?.id == offers[i]?.store?.id) || null;
      if (!idExisted) {
        showOffers.push(offers[i]);
      }
    }
    return showOffers.sort(() => Math.random() - 0.5);
  } catch {
    return [];
  }
}

type PickParms = {
  /** Maximum number of items to pick */
  max_items: number;
  /** Maximum picks by same publisher */
  max_per_store: number;
  /** Shuffle before picking */
  shuffle: boolean;
}

/**
 * Picks a number of offers from a list of offers
 * @param offers 
 * @param params 
 * @returns 
 */
export function pickOffers<T extends ManageAPI.Offer>(offers: T[], params: PickParms): T[] {
  const store_limit = new Map<number, number>();
  const result: T[] = [];

  try {
    // Copy if shuffle is needed
    const initial = params.shuffle ? [...offers] : offers;

    params.shuffle && fisherYatesShuffle(initial);

    for (const offer of initial) {
      const store_id = offer.store?.id || 0;
      const limit = store_limit.get(store_id) || 0;
      if (limit < params.max_per_store) {
        result.push(offer);
        store_limit.set(store_id, limit + 1);
      }

      if (result.length >= params.max_items) {
        break;
      }
    }
    return result;

  } catch (error) {
    console.error(error);
    return offers;
  }
}

function hasStream(offer: ManageAPI.Offer, id: number) {
  return (offer.publicationStreams ?? []).some(s => s.id == id);
}

/**
 * Filters Offers by a specific publication stream
 * @param offers 
 * @param stream - the publication stream id
 * @param include_orphans - include offers with no publication streams
 * @returns 
 */
export function filterOffersByStream<T extends ManageAPI.Offer>(offers: readonly T[], stream: number, include_orphans = false): T[] {

  return offers
    .filter((offer) => {
      const streams = offer.publicationStreams ?? [];
      return !include_orphans
        ? hasStream(offer, stream)
        : hasStream(offer, stream) || streams.length == 0;
    });
}

export function collectOfferStreams<T extends ManageAPI.Offer>(offers: readonly T[]): number[] {
  const result = new Set<number>();
  offers.forEach(offer => {
    const streams = offer.publicationStreams || [];
    streams.forEach(x => result.add(x.id));
  }, []);
  return Array.from(result);
}

/**
 * Filters stream toggles by available streams
 * @param toggles Whatever types that are used to set up stream toggles
 * @param offers 
 * @returns 
 */
export function reduceByAvailableStreams<T extends { id: number }, O extends ManageAPI.Offer>(toggles: T[], offers: readonly O[]): T[] {
  return toggles.reduce((acc: T[], toggle) => {
    if (toggle.id == 0) {
      acc.push(toggle);
    }
    else {
      const match = offers.some(x => hasStream(x, toggle.id));
      if (match) {
        acc.push(toggle);
      }
    }
    return acc;
  }, []);

}
